import React from "react";
import { LibraryItem } from "@/contexts/UniversalLibraryContext/types";
import { Card } from "@/components/ui/card";
import { OptimizedImage } from "@/components/ui/OptimizedImage";
import { motion } from "framer-motion";
import AddToLibraryButton from "./AddToLibraryButton";
import GlassPill from "@/components/ui/GlassPill";
import MoveCardButton from "@/components/ui/MoveCardButton";
import { getContentTypeConfig } from "./utils/contentTypeConfig";
import AddToProjectButton from "@/components/private-image-generator/AddToProjectButton";
import { PublicImageAddToProjectButton } from "@/components/image-generator";
import ResearchAddToProjectButton from "@/components/private-research-generator/AddToProjectButton";
import { PublicResearchAddToProjectButton } from "@/components/research-generator";
import { stripMarkdownAndTruncate } from "@/components/research-generator/research-matrix-utils/content-utils";
import PageAddToProjectButton from "@/components/private-text-generator/PageAddToProjectButton";
import { Clock, FileText as FileTextIcon } from 'lucide-react';

// Helper functions for document metadata
const calculateWordCount = (content?: string): number => {
  if (!content) return 0;
  return content.split(/\s+/).filter(word => word.length > 0).length;
};

const calculateReadingTime = (wordCount: number): number => {
  return Math.max(1, Math.ceil(wordCount / 200));
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) {
    return 'Today';
  } else if (diffInDays === 1) {
    return 'Yesterday';
  } else if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  } else {
    return date.toLocaleDateString();
  }
};

interface Props {
  item: LibraryItem;
  onClick?: (item: LibraryItem) => void;
  // Optional drag-and-drop props
  isDraggable?: boolean;
  dragHandleProps?: Record<string, any>;
  isDragging?: boolean;
}



const UniversalItemCard: React.FC<Props> = ({ 
  item, 
  onClick, 
  isDraggable = false, 
  dragHandleProps, 
  isDragging = false 
}) => {
  const contentConfig = getContentTypeConfig(item.contentType);
  const IconComponent = contentConfig.icon;

  // If dragging, show placeholder
  if (isDragging) {
    return (
      <div className="opacity-50 bg-gray-200 dark:bg-gray-700 border-2 border-dashed border-purple-400 rounded-lg aspect-square" />
    );
  }

  return (
    <motion.div
      whileHover={{ y: -4, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1.0] }}
      className="group cursor-pointer"
      onClick={() => onClick?.(item)}
      tabIndex={0}
      aria-label={item.title}
      role="button"
    >
      <Card className="relative overflow-hidden aspect-square border-0 shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-white to-gray-50/80 dark:from-gray-900 dark:to-gray-800/80 backdrop-blur-sm">
        {/* Bottom Left: Content Type Badge with Label */}
        <div className="absolute bottom-2 left-2 z-20">
          <div className={`
            relative flex items-center gap-2 px-2 py-1 rounded-md
            backdrop-blur-md border border-white/20
            transition-all duration-300 ease-out
            shadow-lg
            before:absolute before:inset-0 before:rounded-md
            before:bg-gradient-to-br before:from-white/15 before:to-transparent
            after:absolute after:inset-0 after:rounded-md
            after:bg-gradient-to-t after:from-black/5 after:to-transparent
            group-hover:scale-105
            transform-gpu
          `}
          style={{
            background: `${
              contentConfig.variant === 'blue' ? 'rgba(59, 130, 246, 0.3)' :
              contentConfig.variant === 'green' ? 'rgba(34, 197, 94, 0.3)' :
              contentConfig.variant === 'purple' ? 'rgba(147, 51, 234, 0.3)' :
              contentConfig.variant === 'orange' ? 'rgba(249, 115, 22, 0.3)' :
              'rgba(107, 114, 128, 0.3)'
            }`
          }}>
            <IconComponent size={14} className="text-white/90 drop-shadow-sm flex-shrink-0" />
            <span className="text-white/90 text-xs font-medium drop-shadow-sm">
              {contentConfig.label}
            </span>
          </div>
        </div>

        {/* Top Right: Drag Handle (only when draggable) */}
        {isDraggable && dragHandleProps && (
        <div className="absolute top-2 right-2 z-20">
            <div
              {...dragHandleProps}
              className="cursor-grab active:cursor-grabbing touch-none"
              onClick={(e) => e.stopPropagation()}
            >
              <MoveCardButton
                variant="glass"
                size="sm"
                iconType="move"
                className="opacity-70 hover:opacity-100 transition-opacity duration-200"
                title="Drag to reorder"
              />
            </div>
          </div>
        )}

        {/* Bottom Right: Project and Storage buttons side by side */}
        <div className="absolute bottom-2 right-2 z-30 flex flex-row gap-2">
          {/* Add to Project button (top) - for images, research, and pages */}
          {(item.contentType === 'private_image' || item.contentType === 'image' || item.contentType === 'private_research' || item.contentType === 'research' || item.contentType === 'private_page') && (
            item.contentType === 'private_image' ? (
              <AddToProjectButton
                itemId={item.originalItem?.id || item.id}
                contentType="private_image"
                variant="storage" 
                forceWhiteText={true}
                dropdownLabel="Add to Project"
              />
            ) : item.contentType === 'private_research' ? (
              <ResearchAddToProjectButton
                itemId={item.originalItem?.id || item.id}
                variant="auto"
                forceWhiteText={true}
                dropdownLabel="Add to Project"
              />
            ) : item.contentType === 'research' ? (
              <PublicResearchAddToProjectButton
                itemId={item.originalItem?.id || item.id}
                variant="auto"
                forceWhiteText={true}
                dropdownLabel="Add to Project"
              />
            ) : item.contentType === 'private_page' ? (
              <PageAddToProjectButton
                pageId={item.originalItem?.id || item.id}
                dropdownLabel="Add to Project"
                variant="auto"
                currentProjectId={item.originalItem?.workbook_id}
              />
            ) : (
              <PublicImageAddToProjectButton
                itemId={item.originalItem?.id || item.id}
                variant="storage"
                forceWhiteText={true}
                dropdownLabel="Add to Project"
              />
            )
          )}
          
          {/* Add to Folder button */}
          <AddToLibraryButton
              itemId={item.contentType === 'workbook' ? item.id : (item.originalItem?.id || item.id)}
              contentType={item.contentType as 'image' | 'research' | 'workbook' | 'app_idea' | 'private_image' | 'private_research' | 'private_page'}
              dropdownLabel="Add to Folder"
            variant="storage"
            />
        </div>

        {/* Main Content Area */}
        <div className="relative w-full h-full">
          {item.imageUrl ? (
            item.contentType === 'workbook' ? (
              // Workbook with Cover Image - Hybrid layout
              <div className="w-full h-full relative overflow-hidden rounded-lg">
                {/* Image fills remaining space above title area */}
                <div className="absolute inset-0 top-0 bottom-10 overflow-hidden">
                  <OptimizedImage
                    src={item.imageUrl}
                    alt={item.title}
                    className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
                    style={{ objectPosition: 'center' }}
                  />
                </div>
                
                {/* Colored Background Section for title area */}
                <div className={`absolute inset-0 top-auto bottom-0 h-10 ${
                  contentConfig.variant === 'purple' ? 'bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/40 dark:to-purple-800/40' :
                  'bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-900/40 dark:to-gray-800/40'
                }`} />
                

              </div>
            ) : (
              // Regular Image Content - Full bleed for pure images
              <div className="w-full h-full relative overflow-hidden rounded-lg">
                <OptimizedImage
                  src={item.imageUrl}
                  alt={item.title}
                  className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-110"
                  aspectRatio="1/1"
                />

              </div>
            )
          ) : (
            // Non-image Content - Research, Pages, and other content
            <div className="w-full h-full relative overflow-hidden rounded-lg">
              {/* Background gradient - subtle and beautiful */}
              <div className={`absolute inset-0 ${
                item.contentType === 'research' || item.contentType === 'private_research'
                  ? // Research cards get subtle gradients
                    item.contentType === 'research'
                    ? 'bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50 dark:from-emerald-950/30 dark:via-green-950/30 dark:to-teal-950/30'
                    : 'bg-gradient-to-br from-purple-50 via-violet-50 to-indigo-50 dark:from-purple-950/30 dark:via-violet-950/30 dark:to-indigo-950/30'
                  : item.contentType === 'private_page'
                  ? // Document cards get purple gradients
                    'bg-gradient-to-br from-purple-50 via-purple-50 to-violet-50 dark:from-purple-950/30 dark:via-purple-950/30 dark:to-violet-950/30'
                  : // Other content types get themed gradients
                    contentConfig.variant === 'blue' ? 'bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950/30 dark:to-blue-900/30' :
                    contentConfig.variant === 'green' ? 'bg-gradient-to-br from-green-50 to-green-100 dark:from-green-950/30 dark:to-green-900/30' :
                    contentConfig.variant === 'purple' ? 'bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950/30 dark:to-purple-900/30' :
                    contentConfig.variant === 'orange' ? 'bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-950/30 dark:to-orange-900/30' :
                    'bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-950/30 dark:to-gray-900/30'
              }`} />
              
              {/* Text overlay gradient for readability */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-black/10" />
              
              {/* Content area - positioned like image card text */}
              <div className="absolute inset-0 p-4 flex flex-col justify-start z-10">
                {/* Title and description in upper area */}
                <div className="space-y-2 max-w-full">
                  <h3 className="font-semibold text-gray-900 dark:text-white text-base leading-tight drop-shadow-sm"
                      style={{
                        display: '-webkit-box',
                        WebkitBoxOrient: 'vertical',
                        WebkitLineClamp: 2,
                        lineHeight: '1.3',
                        overflow: 'hidden'
                      }}>
                    {item.title}
                  </h3>
                  
                  {/* Document metadata for private_page */}
                  {item.contentType === 'private_page' && item.originalItem && (
                    <div className="space-y-1">
                      {/* Page type display */}
                      {item.originalItem.type && (
                        <div className="flex items-center gap-1">
                          <FileTextIcon className="h-3 w-3 text-purple-600 dark:text-purple-400" />
                          <span className="text-xs font-medium text-purple-700 dark:text-purple-300">
                            {item.originalItem.type}
                          </span>
                        </div>
                      )}
                      
                      {/* Document stats */}
                      <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                        <Clock className="h-3 w-3" />
                        <span>{formatDate(item.originalItem.updated_at || item.createdAt)}</span>
                        <span className="mx-1">•</span>
                        <span>{calculateWordCount(item.originalItem.content)} words</span>
                        <span className="mx-1">•</span>
                        <span>{calculateReadingTime(calculateWordCount(item.originalItem.content))}m read</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Description for non-document content */}
                  {item.contentType !== 'private_page' && item.description && (
                    <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed drop-shadow-sm"
                       style={{
                         display: '-webkit-box',
                         WebkitBoxOrient: 'vertical',
                         WebkitLineClamp: 3,
                         lineHeight: '1.4',
                         overflow: 'hidden'
                       }}>
                      {(item.contentType === 'research' || item.contentType === 'private_research') 
                        ? stripMarkdownAndTruncate(item.description, 150)
                        : item.description}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Background icon for non-research and non-document content */}
              {!(item.contentType === 'research' || item.contentType === 'private_research' || item.contentType === 'private_page') && (
                <div className="absolute inset-0 flex items-center justify-center z-0">
                  <IconComponent size={64} className={`opacity-20 ${
                    contentConfig.variant === 'blue' ? 'text-blue-500' :
                    contentConfig.variant === 'green' ? 'text-green-500' :
                    contentConfig.variant === 'purple' ? 'text-purple-500' :
                    contentConfig.variant === 'orange' ? 'text-orange-500' :
                    'text-gray-500'
                  }`} />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Hover overlay effect */}
        <div className="absolute inset-0 bg-gradient-to-t from-purple-500/0 to-purple-500/0 group-hover:from-purple-500/10 group-hover:to-purple-500/5 transition-all duration-300 pointer-events-none" />
      </Card>
    </motion.div>
  );
};

export default UniversalItemCard;
